import { Suspense } from "react";

import { defineMessage, defineMessages, useIntl } from "react-intl";
import { JiraSoftwarePricingPageProps } from "src/pages/software/jira/pricing/jira-software-pricing-page";

import {
    PriceFinderMessages,
    ProductPlan
} from "@/components/wpl/pricing/src/price-finder/constants";
import PriceFinderSection from "@/components/wpl/pricing/src/price-finder/price-finder";

import AdvancedAdmin from "../expander-contents/advanced-admin";
import AtlassianIntelligence from "../expander-contents/atlassian-intelligence";
import Automations from "../expander-contents/automations";
import Backlog from "../expander-contents/backlog";
import CrossProductInsights from "../expander-contents/cross-product-insights";
import CrossTeamPlanning from "../expander-contents/cross-team-planning";
import CustomizableApprovalProcesses from "../expander-contents/customizable-approval-processes";
import EnterpriseIam from "../expander-contents/enterprise-iam";
import EnterpriseSupport from "../expander-contents/enterprise-support";
import ExternalCollaboration from "../expander-contents/external-collaboration";
import MultiRegionDataResidency from "../expander-contents/multi-region-data-residency";
import MultipleInstances from "../expander-contents/multiple-instances";
import ReportsAndDashboards from "../expander-contents/reports-and-dashboards";
import CommunitySupport from "../expander-contents/support-free";
import PremiumSupport from "../expander-contents/support-premium";
import SupportStandard from "../expander-contents/support-standard";
import UnlimitedProjects from "../expander-contents/unlimited-projects";
import UnlimitedUsers from "../expander-contents/unlimited-users";
import UserRolesPermissions from "../expander-contents/user-roles-permissions";

const JiraPriceFinderTeam24 = (props: JiraSoftwarePricingPageProps) => {
    const intl = useIntl();

    const newTagText = intl.formatMessage({
        id: "JiraSoftwarePricingPage.priceFinder.newTagText",
        defaultMessage: "New"
    });

    const freePlanFeatureMessages = [
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.unlimitedProjectBoards.title.v2",
                    defaultMessage:
                        "Unlimited goals, projects, tasks, and forms"
                }),
                content: UnlimitedProjects
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.backlogAndTimeline.title",
                    defaultMessage:
                        "Backlog, list, board, timeline, calendar, and summary views"
                }),
                content: Backlog
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.reportsAndDashboards.title",
                    defaultMessage: "Reports and dashboards"
                }),
                content: ReportsAndDashboards
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.freeAutomations.title",
                    defaultMessage: "100 automations per site per month"
                }),
                content: Automations
            }
        },
        {
            featureMessage: intl.formatMessage({
                id: "JiraSoftwarePricingPage.priceFinder.freePlan2GBOfStorage",
                defaultMessage: "2 GB of storage"
            })
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.freeCommunitySupport.title",
                    defaultMessage: "Support from Atlassian Community"
                }),
                content: CommunitySupport
            }
        },
        {
            featureMessage: intl.formatMessage({
                id: "JiraSoftwarePricingPageTeam24.features.upTo10Users.title",
                defaultMessage: "Up to 10 users"
            })
        }
    ];

    const standardPlanFeatureMessages = [
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.userRolesPermissions.title",
                    defaultMessage: "User roles and permissions"
                }),
                content: UserRolesPermissions
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.externalCollaboration.title",
                    defaultMessage: "External collaboration"
                }),
                content: ExternalCollaboration
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.multiRegionDataResidency.title",
                    defaultMessage: "Multi-region data residency"
                }),
                content: MultiRegionDataResidency
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.standardAutomations.title",
                    defaultMessage: "1,700 automations per site per month"
                }),
                content: Automations
            }
        },
        {
            featureMessage: intl.formatMessage({
                id: "JiraSoftwarePricingPageTeam24.features.standardStorage.title",
                defaultMessage: "250 GB of storage"
            })
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.standardSupport.title",
                    defaultMessage: "9/5 regional support"
                }),
                content: SupportStandard
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.standardUnlimitedUsers.title",
                    defaultMessage: "Unlimited users"
                }),
                content: UnlimitedUsers
            }
        }
    ];

    const premiumPlanFeatureMessages = [
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.aiPowered.title.v2",
                    defaultMessage:
                        "Generate, summarize, and search content with Atlassian Intelligence (AI)"
                }),
                content: AtlassianIntelligence
            },
            lozengeText: newTagText
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.crossTeamPlanning.title",
                    defaultMessage:
                        "Cross-team planning and dependency management"
                }),
                content: CrossTeamPlanning
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.customizableApprovalProcesses.title",
                    defaultMessage: "Customizable approval processes"
                }),
                content: CustomizableApprovalProcesses
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.premiumAutomation.title",
                    defaultMessage:
                        "Per user automation limits (1000 per month)"
                }),
                content: Automations
            }
        },
        {
            featureMessage: intl.formatMessage({
                id: "JiraSoftwarePricingPageTeam24.features.unlimitedStorage.title",
                defaultMessage: "Unlimited storage"
            })
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.premiumSupport.title",
                    defaultMessage: "24/7 support for critical issues"
                }),
                content: PremiumSupport
            }
        },
        {
            featureMessage: intl.formatMessage({
                id: "JiraSoftwarePricingPageTeam24.features.premiumSla.title",
                defaultMessage: "99.9% uptime SLA"
            })
        }
    ];

    const enterprisePlanFeatureMessages = [
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.crossProductInsights.title",
                    defaultMessage:
                        "Cross-product insights with Atlassian Analytics and Data Lake"
                }),
                content: CrossProductInsights
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.advancedAdmin.title",
                    defaultMessage: "Advanced admin controls and security"
                }),
                content: AdvancedAdmin
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.enterpriseIAM.title",
                    defaultMessage:
                        "Enterprise-grade identity and access management"
                }),
                content: EnterpriseIam
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.enterpriseAutomations.title",
                    defaultMessage: "Unlimited automations"
                }),
                content: Automations
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.multipleInstances.title",
                    defaultMessage: "Multiple instances (up to 150)"
                }),
                content: MultipleInstances
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePricingPageTeam24.features.enterpriseSupport.title",
                    defaultMessage: "24/7 support for all issues"
                }),
                content: EnterpriseSupport
            }
        },
        {
            featureMessage: intl.formatMessage({
                id: "JiraSoftwarePricingPageTeam24.features.enterpriseSLA.title",
                defaultMessage: "99.95% uptime SLA"
            })
        }
    ];

    // planNames should not be translated
    const jswProductPlans: ProductPlan<"jiraSoftware">[] = [
        {
            planName: "Free",
            offeringSku: "jira-software-free",
            description: intl.formatMessage({
                id: "JiraSoftwarePricingPageTeam24.priceFinder.freeDescription",
                defaultMessage: "Free forever for 10 users"
            }),
            recommendationLimit: 0,
            signupLimit: 50000,
            signupURLSuffix: "?bundle=jira-software&edition=free",
            planFeatureMessages: freePlanFeatureMessages
        },
        {
            planName: "Standard",
            offeringSku: "jira-software-standard",
            description: intl.formatMessage({
                id: "JiraSoftwarePricingPageTeam24.priceFinder.standardDescription",
                defaultMessage: "Everything you need to get started"
            }),
            recommendationLimit: 10,
            signupLimit: 50000,
            signupURLSuffix: "?bundle=jira-software&edition=standard",
            planFeatureMessages: standardPlanFeatureMessages
        },
        {
            planName: "Premium",
            offeringSku: "jira-software-premium",
            description: intl.formatMessage({
                id: "JiraSoftwarePricingPageTeam24.priceFinder.premiumDescription",
                defaultMessage: "Align multiple teams"
            }),
            recommendationLimit: 250,
            signupLimit: 50000,
            signupURLSuffix: "?bundle=jira-software&edition=premium",
            planFeatureMessages: premiumPlanFeatureMessages
        },
        {
            planName: "Enterprise",
            recommendationLimit: 5000,
            description: intl.formatMessage({
                id: "JiraSoftwarePricingPageTeam24.priceFinder.enterpriseDescription",
                defaultMessage:
                    "Advanced analytics, scale and security for enterprises"
            }),
            signupLimit: 50000,
            planFeatureMessages: enterprisePlanFeatureMessages
        }
    ];

    const messages: PriceFinderMessages = defineMessages({
        unitCountInputText: {
            id: "JiraSoftwarePricingPage.priceFinder.unitCountInputText",
            defaultMessage: "How many users are on your team?"
        },
        unitCountInputTextRedesign: {
            id: "JiraSoftwarePricingPage.priceFinder.unitCountInputTextRedesign",
            defaultMessage: "Team size:"
        },
        unitCountInputTerm: {
            id: "JiraSoftwarePricingPage.priceFinder.unitCountInputTerm",
            defaultMessage: "users"
        },
        billingCycleText: {
            id: "JiraSoftwarePricingPage.priceFinder.billingCycleText",
            defaultMessage: "Billing cycle:"
        },
        unitCountInputTooltip: {
            id: "JiraSoftwarePricingPage.priceFinder.unitCountInputTooltip",
            defaultMessage:
                "A Jira Software user is any user who can log in to Jira Software, and who exists in User Management"
        },
        monthlyExampleHelpText: {
            id: "JiraSoftwarePricingPage.priceFinder.monthlyExampleHelpText",
            defaultMessage:
                "Monthly plans give you flexibility as your number of active users changes, so pay for seats as you use them. Remember that the number of seats may change the total price."
        },
        annualExampleHelpText: {
            id: "JiraSoftwarePricingPage.priceFinder.annualExampleHelpText",
            defaultMessage:
                "Annual plans give you flexibility and help you prepare for growth. Buy a group of seats at a flat rate now, then fill your empty seats through the year at no extra cost."
        },
        exampleStandardPlan: {
            id: "JiraSoftwarePricingPage.priceFinder.exampleStandardPlan",
            defaultMessage: "Pricing for {unitCount} users on Standard plan",
            description: "Standard is a plan name and should not be translated"
        },
        monthlyExampleUnitCountText: {
            id: "JiraSoftwarePricingPage.priceFinder.monthlyExampleUserCount",
            defaultMessage: "User count"
        },
        monthlyExampleUnitCountAt: {
            id: "JiraSoftwarePricingPage.priceFinder.monthlyExampleUnitCountAt",
            defaultMessage: "users @",
            description: "will be used as follows: 100 users @ $8.15"
        },
        monthlyExampleUnitCountTotal: {
            id: "JiraSoftwarePricingPage.priceFinder.monthlyExampleUnitCount",
            defaultMessage: "{unitCount} users"
        },
        monthlyExampleEstimatedPrice: {
            id: "JiraSoftwarePricingPage.priceFinder.monthlyExampleEstimatedPrice",
            defaultMessage: "Estimated price per user: <strong>{price}</strong>"
        },
        annualExampleUnitCountTierText: {
            id: "JiraSoftwarePricingPage.priceFinder.annualExampleUnitCountTierText",
            defaultMessage: "User tier"
        },
        annualExampleEstimatedPrice: {
            id: "JiraSoftwarePricingPage.priceFinder.annualExampleEstimatedPrice",
            defaultMessage:
                "Estimated price per user/per month: <strong>{price}</strong>"
        },
        cardOverSignupLimit: {
            id: "JiraSoftwarePricingPage.priceFinder.cardOverSignupLimit",
            defaultMessage: "Your user count exceeds this plan's limit"
        },
        cardEnterpriseMinimumCount: {
            id: "JiraSoftwarePricingPage.priceFinder.cardEnterpriseMinimumCount",
            defaultMessage:
                "Enterprise plan pricing is available when you enter {enterpriseMinimumCount} or more users above",
            description:
                "Enterprise is a plan name and should not be translated"
        },
        cardUnitCountTierRange: {
            id: "JiraSoftwarePricingPage.priceFinder.cardUnitCountTierRange",
            defaultMessage: "User tier: {lowerLimit} - {upperLimit}"
        },
        cardMonthlyPerUnitCount: {
            id: "JiraSoftwarePricingPageTeam24.priceFinder.cardMonthlyPerUnitCount",
            defaultMessage: "per user / month"
        },
        cardFreeDescription: {
            id: "JiraSoftwarePricingPage.priceFinder.cardFreeDescription",
            defaultMessage: "Always free for 10 users"
        },
        freeLimitModalTitle: {
            id: "JiraSoftwarePricingPage.priceFinder.freeLimitModalTitle",
            defaultMessage: "Free has a {freeLimitCount}-user limit",
            description: "Free is a plan name and should not be translated"
        },
        freeLimitModalMessage: {
            id: "JiraSoftwarePricingPage.priceFinder.freeLimitModalMessage",
            defaultMessage:
                "If you'd like to add more than {freeLimitCount} users, consider another plan instead."
        }
    });

    messages.headerText = defineMessage({
        id: "JiraSoftwarePricingPageTeam24.priceFinder.header",
        defaultMessage:
            "<blue>Simple, transparent pricing</blue> for every team."
    });

    return (
        <Suspense>
            <PriceFinderSection
                productPlans={jswProductPlans}
                hamsProductKey="jira-software.ondemand"
                messages={messages}
                serverSideCCPPricing={
                    props.pricing?.ccpPricingData?.jiraSoftware
                }
                serverSideHAMSPricing={
                    props.pricing?.hamsPricingData?.jiraSoftware
                }
                countryCode={props.countryCode}
                dataProductKey="jiraSoftware"
                defaultUnitCount={300}
            />
        </Suspense>
    );
};

export default JiraPriceFinderTeam24;
